import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Bell, PackageCheck, ClipboardCheck, Truck, Check, X, RotateCcw } from "lucide-react";
import { api, apiError, downloadFile } from "@/lib/api";
import { fmtNum, fmtMoney, fmtDate, today } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const STOCK_TONE = { "CRITICAL LOW STOCK": "bad", "OUT OF STOCK": "bad", "REORDER SOON": "warn", HEALTHY: "good" };
const RFQ_TONE = { "Pending HOD": "warn", "Pending PM": "warn", Approved: "good", Rejected: "bad", "Revision Required": "primary" };

export default function Store() {
  const { pid } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("inventory");
  const [materials, setMaterials] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [grns, setGrns] = useState([]);
  const [cons, setCons] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [meta, setMeta] = useState({ material_categories: [], units: [] });
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});

  const load = () => {
    api.get("/store/materials", { params: { project_id: pid } }).then(({ data }) => setMaterials(data));
    api.get("/store/rfqs", { params: { project_id: pid } }).then(({ data }) => setRfqs(data));
    api.get("/store/grns", { params: { project_id: pid } }).then(({ data }) => setGrns(data));
    api.get("/store/consumption", { params: { project_id: pid } }).then(({ data }) => setCons(data));
    api.get("/store/alerts", { params: { project_id: pid } }).then(({ data }) => setAlerts(data));
    api.get("/meta").then(({ data }) => setMeta(data));
  };
  useEffect(() => { load(); }, [pid]);

  const open = (kind) => {
    const defaults = {
      material: { code: "", name: "", category: "Cement", unit: "MT", min_stock: 0, lead_time_days: 7, unit_rate: 0, location: "Main Store" },
      rfq: { item_code: "", item_name: "", specification: "", qty_required: "", unit: "MT", purpose_wbs: "", required_date: today(), priority: "Normal" },
      grn: { material_id: "", po_number: "", invoice_number: "", supplier: "", delivered_qty: "", accepted_qty: "", rejected_qty: 0, qc_signoff: "", remarks: "", receipt_date: today() },
      consumption: { material_id: "", issued_qty: "", issued_to: "", work_front: "", date: today(), remarks: "" },
    };
    setForm(defaults[kind]);
    setDialog(kind);
  };

  const submit = async () => {
    const paths = { material: "/store/materials", rfq: "/store/rfqs", grn: "/store/grns", consumption: "/store/consumption" };
    const numeric = ["min_stock", "lead_time_days", "unit_rate", "qty_required", "delivered_qty", "accepted_qty", "rejected_qty", "issued_qty"];
    const body = { project_id: pid, ...form };
    numeric.forEach((k) => { if (k in body) body[k] = Number(body[k] || 0); });
    try {
      await api.post(paths[dialog], body);
      toast.success(dialog === "consumption" ? "Issued — stock balance updated & alerts checked" : "Saved");
      setDialog(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const act = async (rfq, tier, action) => {
    try {
      await api.put(`/store/rfqs/${rfq.id}/${tier}`, { action, remarks: `${action} by ${user?.name}` });
      toast.success(`RFQ ${action}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const stockValue = materials.reduce((s, m) => s + (m.stock_value || 0), 0);

  return (
    <div data-testid="store-page">
      <PageHead title="Store, RFQ & Inventory Lifecycle" subtitle="Live balances, multi-tier approvals, GRN receipts, daily issue logs and safety-stock alerts.">
        <Button variant="outline" onClick={() => api.post(`/store/alerts/scan?project_id=${pid}`).then(({ data }) => { toast.success(`${data.alerts_fired} alerts fired`); load(); })} data-testid="store-scan-btn">
          <Bell className="w-4 h-4 mr-1.5" /> Run alert scan
        </Button>
        <Button variant="outline" onClick={() => downloadFile(`/reports/excel/${pid}`, "store_audit.xlsx")} data-testid="store-export-btn">Audit Excel</Button>
      </PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        <Kpi label="Stock items" value={materials.length} testId="kpi-store-items" />
        <Kpi label="Stock value" value={fmtMoney(stockValue)} tone="primary" />
        <Kpi label="Critical low" value={materials.filter((m) => m.stock_status !== "HEALTHY" && m.stock_status !== "REORDER SOON").length} tone="bad" testId="kpi-store-critical" />
        <Kpi label="RFQ awaiting approval" value={rfqs.filter((r) => r.status.startsWith("Pending")).length} tone="warn" testId="kpi-rfq-pending" />
        <Kpi label="Active alerts" value={alerts.length} tone={alerts.length ? "bad" : "good"} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-3">
        <TabsList data-testid="store-tabs">
          <TabsTrigger value="inventory" data-testid="store-tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="rfq" data-testid="store-tab-rfq">RFQ & Approvals</TabsTrigger>
          <TabsTrigger value="grn" data-testid="store-tab-grn">GRN Receipts</TabsTrigger>
          <TabsTrigger value="consumption" data-testid="store-tab-consumption">Daily Consumption</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="store-tab-alerts">Alert Engine</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "inventory" ? (
        <Panel title="Live store material availability" testId="inventory-panel"
          action={<Button size="sm" onClick={() => open("material")} data-testid="material-new-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add material</Button>}>
          <div className="overflow-x-auto max-h-[62vh]">
            <table className="w-full dense-table">
              <thead className="sticky-head"><tr><th>Code</th><th>Material</th><th>Category</th><th>Unit</th><th className="text-right">Received</th><th className="text-right">Consumed</th><th className="text-right">Balance</th><th className="text-right">Min stock</th><th className="text-right">Lead time</th><th className="text-right">Value</th><th>Status</th><th>Location</th></tr></thead>
              <tbody>
                {materials.map((m) => (
                  <tr key={m.id} data-testid={`material-row-${m.code}`}>
                    <td className="num text-primary">{m.code}</td>
                    <td className="max-w-[220px] truncate">{m.name}</td>
                    <td className="text-muted-foreground">{m.category}</td>
                    <td className="text-muted-foreground">{m.unit}</td>
                    <td className="num text-right">{fmtNum(m.received_qty, 1)}</td>
                    <td className="num text-right">{fmtNum(m.consumed_qty, 1)}</td>
                    <td className="num text-right font-semibold">{fmtNum(m.balance_qty, 1)}</td>
                    <td className="num text-right text-muted-foreground">{fmtNum(m.min_stock, 1)}</td>
                    <td className="num text-right text-muted-foreground">{m.lead_time_days}d</td>
                    <td className="num text-right">{fmtMoney(m.stock_value)}</td>
                    <td><Badge2 tone={STOCK_TONE[m.stock_status]}>{m.stock_status}</Badge2></td>
                    <td className="text-muted-foreground">{m.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      {tab === "rfq" ? (
        <Panel title="Engineer RFQ / Indent with multi-tier approval matrix" testId="rfq-panel"
          action={<Button size="sm" onClick={() => open("rfq")} data-testid="rfq-new-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Raise RFQ</Button>}>
          <div className="space-y-2">
            {rfqs.map((r) => (
              <div key={r.id} className="border border-border p-3" data-testid={`rfq-card-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge2 tone={RFQ_TONE[r.status]} testId={`rfq-status-${r.id}`}>{r.status}</Badge2>
                  <span className="num text-[12px] text-primary">{r.item_code}</span>
                  <span className="text-[13px] font-medium">{r.item_name}</span>
                  <span className="num text-[12px] text-muted-foreground">· {fmtNum(r.qty_required, 1)} {r.unit}</span>
                  <Badge2 tone={r.priority === "Urgent" ? "bad" : r.priority === "High" ? "warn" : "muted"}>{r.priority}</Badge2>
                  <span className="ml-auto text-[11px] text-muted-foreground">Store at raise: <span className="num">{fmtNum(r.store_available_at_raise, 1)}</span> · Need by {fmtDate(r.required_date)}</span>
                </div>
                <div className="text-[12px] text-muted-foreground mt-1.5">WBS {r.purpose_wbs} · Raised by {r.raised_by_name} · {r.specification}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(r.approvals || []).map((a, i) => (
                    <span key={i} className="text-[11px] border border-border px-1.5 py-0.5 rounded bg-muted/50">
                      {a.tier}: <strong>{a.action}</strong>{a.remarks ? ` — ${a.remarks}` : ""}
                    </span>
                  ))}
                </div>
                {r.status === "Pending HOD" && ["hod", "admin"].includes(user?.role) ? (
                  <div className="flex gap-2 mt-2.5">
                    <Button size="sm" onClick={() => act(r, "hod", "Approved")} data-testid={`rfq-hod-approve-${r.id}`}><Check className="w-3.5 h-3.5 mr-1" /> HOD approve</Button>
                    <Button size="sm" variant="outline" onClick={() => act(r, "hod", "Revision Required")} data-testid={`rfq-hod-revise-${r.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Revision</Button>
                    <Button size="sm" variant="destructive" onClick={() => act(r, "hod", "Rejected")} data-testid={`rfq-hod-reject-${r.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                  </div>
                ) : null}
                {r.status === "Pending PM" && ["pm", "admin"].includes(user?.role) ? (
                  <div className="flex gap-2 mt-2.5">
                    <Button size="sm" onClick={() => act(r, "pm", "Approved")} data-testid={`rfq-pm-approve-${r.id}`}><Check className="w-3.5 h-3.5 mr-1" /> PM approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => act(r, "pm", "Rejected")} data-testid={`rfq-pm-reject-${r.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                  </div>
                ) : null}
              </div>
            ))}
            {rfqs.length === 0 ? <p className="text-sm text-muted-foreground">No indents raised.</p> : null}
          </div>
        </Panel>
      ) : null}

      {tab === "grn" ? (
        <Panel title="Goods Receipt Notes — accepted qty auto-adds to live balance" testId="grn-panel"
          action={<Button size="sm" onClick={() => open("grn")} data-testid="grn-new-btn"><PackageCheck className="w-3.5 h-3.5 mr-1" /> Log GRN</Button>}>
          <div className="overflow-x-auto max-h-[62vh]">
            <table className="w-full dense-table">
              <thead className="sticky-head"><tr><th>Date</th><th>Material</th><th>PO</th><th>Invoice</th><th>Supplier</th><th className="text-right">Delivered</th><th className="text-right">Accepted</th><th className="text-right">Rejected</th><th>QC sign-off</th></tr></thead>
              <tbody>
                {grns.map((g) => (
                  <tr key={g.id} data-testid={`grn-row-${g.id}`}>
                    <td className="num">{fmtDate(g.receipt_date)}</td>
                    <td className="max-w-[200px] truncate">{g.material_name}</td>
                    <td className="num">{g.po_number}</td>
                    <td className="num text-muted-foreground">{g.invoice_number}</td>
                    <td className="text-muted-foreground">{g.supplier}</td>
                    <td className="num text-right">{fmtNum(g.delivered_qty, 1)}</td>
                    <td className="num text-right text-emerald-500">{fmtNum(g.accepted_qty, 1)}</td>
                    <td className="num text-right text-red-500">{fmtNum(g.rejected_qty, 1)}</td>
                    <td className="text-muted-foreground max-w-[180px] truncate">{g.qc_signoff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      {tab === "consumption" ? (
        <Panel title="Daily consumption log (storekeeper issue register)" testId="consumption-panel"
          action={<Button size="sm" onClick={() => open("consumption")} data-testid="consumption-new-btn"><Truck className="w-3.5 h-3.5 mr-1" /> Issue material</Button>}>
          <div className="overflow-x-auto max-h-[62vh]">
            <table className="w-full dense-table">
              <thead className="sticky-head"><tr><th>Date</th><th>Material</th><th className="text-right">Issued</th><th>Unit</th><th className="text-right">Value</th><th>Issued to</th><th>Work front</th><th>Remarks</th></tr></thead>
              <tbody>
                {cons.map((c) => (
                  <tr key={c.id} data-testid={`consumption-row-${c.id}`}>
                    <td className="num">{fmtDate(c.date)}</td>
                    <td className="max-w-[200px] truncate">{c.material_name}</td>
                    <td className="num text-right">{fmtNum(c.issued_qty, 1)}</td>
                    <td className="text-muted-foreground">{c.unit}</td>
                    <td className="num text-right">{fmtMoney(c.value)}</td>
                    <td>{c.issued_to}</td>
                    <td className="text-muted-foreground">{c.work_front}</td>
                    <td className="text-muted-foreground max-w-[200px] truncate">{c.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      {tab === "alerts" ? (
        <Panel title="Critical stock alert engine — multi-channel triggers" testId="alerts-panel">
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className="border border-border p-3 flex flex-wrap items-center gap-2" data-testid={`alert-row-${a.id}`}>
                <Badge2 tone={a.severity === "Critical" ? "bad" : "warn"}>{a.severity}</Badge2>
                <span className="text-[13px]">{a.title}</span>
                <span className="ml-auto flex gap-1.5">
                  {a.channels.map((c) => <Badge2 key={c} tone="primary">{c}</Badge2>)}
                </span>
                <div className="w-full text-[11px] text-muted-foreground">Notified: {a.recipients.join(" · ")} — {fmtDate(a.created_at)}</div>
              </div>
            ))}
            {alerts.length === 0 ? <p className="text-sm text-muted-foreground">No alerts. All materials above safety stock.</p> : null}
          </div>
        </Panel>
      ) : null}

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="capitalize">{dialog === "grn" ? "Goods Receipt Note" : dialog === "rfq" ? "Raise RFQ / Indent" : dialog === "consumption" ? "Issue material" : "Add material"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {dialog === "material" ? (
              <>
                {[["code", "Item code"], ["name", "Material name"], ["min_stock", "Minimum safety stock"], ["lead_time_days", "Lead time (days)"], ["unit_rate", "Unit rate (₹)"], ["location", "Store location"]].map(([k, l]) => (
                  <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                    <Input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`material-${k}-input`} /></div>
                ))}
                <div className="space-y-1.5"><Label className="text-xs uppercase">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="material-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.material_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="text-xs uppercase">Unit</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="material-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.units.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
              </>
            ) : null}

            {dialog === "rfq" ? (
              <>
                <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Material from store catalogue</Label>
                  <Select value={form.item_code} onValueChange={(v) => {
                    const m = materials.find((x) => x.code === v);
                    setForm({ ...form, item_code: v, item_name: m?.name || "", unit: m?.unit || "Nos" });
                  }}>
                    <SelectTrigger data-testid="rfq-material-select"><SelectValue placeholder="Check availability & select" /></SelectTrigger>
                    <SelectContent>
                      {materials.map((m) => <SelectItem key={m.id} value={m.code}>{m.code} · {m.name} — balance {fmtNum(m.balance_qty, 1)} {m.unit}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                {[["qty_required", "Qty required"], ["purpose_wbs", "Purpose / WBS code"], ["required_date", "Target site date"]].map(([k, l]) => (
                  <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                    <Input type={k === "required_date" ? "date" : "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`rfq-${k}-input`} /></div>
                ))}
                <div className="space-y-1.5"><Label className="text-xs uppercase">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="rfq-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Normal", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Specification</Label>
                  <Textarea rows={2} value={form.specification ?? ""} onChange={(e) => setForm({ ...form, specification: e.target.value })} data-testid="rfq-spec-input" /></div>
              </>
            ) : null}

            {dialog === "grn" ? (
              <>
                <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Material</Label>
                  <Select value={form.material_id} onValueChange={(v) => setForm({ ...form, material_id: v })}>
                    <SelectTrigger data-testid="grn-material-select"><SelectValue placeholder="Select material" /></SelectTrigger>
                    <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                {[["po_number", "PO number"], ["invoice_number", "Invoice number"], ["supplier", "Supplier"], ["receipt_date", "Receipt date"],
                  ["delivered_qty", "Delivered qty"], ["accepted_qty", "Accepted qty"], ["rejected_qty", "Rejected qty"], ["qc_signoff", "QC sign-off"]].map(([k, l]) => (
                  <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                    <Input type={k === "receipt_date" ? "date" : "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`grn-${k}-input`} /></div>
                ))}
              </>
            ) : null}

            {dialog === "consumption" ? (
              <>
                <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Material</Label>
                  <Select value={form.material_id} onValueChange={(v) => setForm({ ...form, material_id: v })}>
                    <SelectTrigger data-testid="consumption-material-select"><SelectValue placeholder="Select material" /></SelectTrigger>
                    <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name} — balance {fmtNum(m.balance_qty, 1)} {m.unit}</SelectItem>)}</SelectContent>
                  </Select></div>
                {[["issued_qty", "Issued qty"], ["issued_to", "Issued to (subcontractor)"], ["work_front", "Work front"], ["date", "Date"]].map(([k, l]) => (
                  <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                    <Input type={k === "date" ? "date" : "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`consumption-${k}-input`} /></div>
                ))}
                <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Remarks</Label>
                  <Textarea rows={2} value={form.remarks ?? ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="consumption-remarks-input" /></div>
              </>
            ) : null}
          </div>
          <DialogFooter><Button onClick={submit} data-testid="store-submit-btn"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
